import { LitElement, css, html, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import '@awesome.me/webawesome/dist/components/input/input.js';
import type WaInput from '@awesome.me/webawesome/dist/components/input/input.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { limitInput } from '../helpers/dom.js';

@customElement('ns-input-field')
export class NSInputFile extends LitElement {
  @query('#input-field')
  private __inputField?: WaInput;

  @query('#tick')
  private __tick?: HTMLElement;

  @property({ type: Boolean })
  disabled?: boolean;

  @property({ type: String })
  value?: string;

  @property({ type: Boolean })
  required?: boolean;

  @property({ type: String })
  width?: string;

  @property({ type: String })
  height?: string;

  @property({ type: String })
  animationType: 'PEEL_BACK' | 'SLIDE_REVEAL' | 'NONE' = 'PEEL_BACK';

  @property({ type: String })
  type?: string;

  @property({ type: String })
  limit?: string;

  @property({ type: String })
  label?: string;

  @property({ type: String })
  placeholder?: string;

  @property({ type: Boolean })
  clearable?: boolean;

  @property({ type: Boolean })
  hideLabel?: boolean;

  // The label above the field rather than beside it, as job panels draw it
  @property({ type: Boolean })
  stacked = false;

  @property({ type: String })
  autocomplete?: string;

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      #input-field {
        --wa-form-control-height: var(--ns-input-height, 30px);
        --wa-form-control-border-radius: var(--wa-border-radius-s);
        --wa-form-control-padding-inline: var(--wa-space-s);
        --wa-form-control-background-color: var(--ns-input-background, var(--ns-theme-grey-dark));
        --wa-form-control-value-color: var(--ns-input-color, var(--ns-theme-white));
        --wa-form-control-value-font-size: var(--wa-font-size-s);
        --wa-form-control-value-font-weight: 400;
        --wa-form-control-value-line-height: normal;
        --wa-color-brand-fill-quiet: var(--wa-form-control-background-color);
      }

      #input-field::part(base) {
        width: var(--ns-input-width, 100%);
        padding-block: 0;
        opacity: 1;
      }

      #input-field::part(base):focus-within {
        outline: 2px solid var(--primary-accent-color);
        outline-offset: -2px;
      }

      #input-field::part(input) {
        width: var(--ns-input-width, auto);
      }

      /* Chrome forces autofilled text to its own (black) field-text colour; keep the field's own colour */
      #input-field::part(input):autofill {
        -webkit-text-fill-color: var(--wa-form-control-value-color);
      }
      #input-field::part(input):-webkit-autofill {
        -webkit-text-fill-color: var(--wa-form-control-value-color);
      }

      #input-field::part(input)::placeholder {
        font-size: var(--wa-font-size-xs);
        color: var(--ns-input-placeholder-color, color-mix(in srgb, var(--ns-theme-white) 85%, transparent));
      }

      .wrapper {
        position: relative;

        z-index: 1;
      }

      #tick {
        position: absolute;
        top: 0;
        left: 0;

        background-color: var(--primary-accent-color);

        height: 100%;
        width: 100%;

        /* The pill radius, the field's own, so the green behind it only shows where the field shrinks
           away on the right, not round its rounded ends */
        border-radius: var(--wa-border-radius-pill);
        /* Only drawn at the right end, where it shows: the tick strip (30px) plus the field's rounded
           end. Under the rest of the field, a hairline of green could show round its edges */
        clip-path: inset(0 0 0 calc(100% - 30px - var(--ns-input-height, 30px)));

        z-index: -1;
      }

      .tick-mark {
        background: var(--ns-theme-container);

        height: 20px;
        width: 20px;

        border-radius: var(--wa-border-radius-l);
      }
      .tick-mark svg {
        width: 50%;
      }
    `,
  ];

  setCustomValidity(message: string) {
    this.__inputField?.setCustomValidity(message);
  }

  checkValidity() {
    return this.__inputField?.checkValidity() ?? true;
  }

  reportValidity() {
    return this.__inputField?.reportValidity() ?? true;
  }

  firstUpdated() {
    if (this.hasAttribute('value') && this.animationType === 'PEEL_BACK') {
      this.__animateInput(true);
    }
    if (this.hasAttribute('value') && this.animationType === 'SLIDE_REVEAL') {
      this.__animateTick(true);
    }
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('value')) {
      this.__animate();
    }
  }

  private __onInput(e: Event) {
    const target = e.target as WaInput;

    if (this.type === 'card-number') {
      this.__applyRegexCardValue(target);
    }

    this.value = target.value ?? '';
    this.dispatchEvent(
      new CustomEvent('input', {
        bubbles: true,
        composed: true,
      }),
    );

    if ((this.type !== 'number' && this.type !== 'card-number') || !this.limit) return;
    const limitArr = this.limit.split(',');
    if (limitArr.length !== 2) return;

    const min = limitArr.shift();
    const max = limitArr.shift();
    if (!min || !max) return;

    const val = limitInput(e as CustomEvent, min, max);
    this.value = val;
  }

  private __animate() {
    if (this.animationType === 'PEEL_BACK') {
      this.__animateInput();
    } else if (this.animationType === 'SLIDE_REVEAL') {
      this.__animateTick();
    }
  }

  private __animateInput(initialValue: boolean = false) {
    if (!this.__inputField) throw new Error('Unable to find input field');

    // Nothing to animate — the field has no value and was never ticked, so there's no
    // "value added" or "value removed" transition to show.
    if (!this.__inputField.value && !this.__inputField.classList.contains('ticked')) return;

    let keyFrames = [{ width: 'calc(100% - 30px)' }, { width: '100%' }];

    if (this.__inputField.value && this.__inputField.classList.contains('ticked')) return;
    if (!this.__inputField.value && !initialValue) this.__inputField.classList.remove('ticked');

    if (this.__inputField.value && !this.__inputField.classList.contains('ticked')) {
      keyFrames = [{ width: '100%' }, { width: 'calc(100% - 30px)' }];
      this.__inputField.classList.add('ticked');
    }

    const timing: KeyframeAnimationOptions = {
      duration: 400,
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      fill: 'forwards',
    };

    this.__tick?.classList.add('active');
    this.__inputField.animate(keyFrames, timing);
  }

  private __animateTick(initialValue: boolean = false) {
    if (!this.__inputField) throw new Error('Unable to find input field');
    if (!this.__tick) throw new Error('Unable to find tick element');

    // Nothing to animate — the field has no value and was never grown, so there's no
    // "value added" or "value removed" transition to show.
    if (!this.__inputField.value && !this.__tick.classList.contains('tick-grown')) return;

    let keyFrames = [{ width: 'calc(100% + 30px)' }, { width: '100%' }];

    if (this.__inputField.value && this.__tick.classList.contains('tick-grown')) return;
    if (!this.__inputField.value && !initialValue) this.__tick.classList.remove('tick-grown');

    if (this.__inputField.value && !this.__tick.classList.contains('tick-grown')) {
      keyFrames = [{ width: '100%' }, { width: 'calc(100% + 30px)' }];
      this.__tick.classList.add('tick-grown');
    }

    const timing: KeyframeAnimationOptions = {
      duration: 400,
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      fill: 'forwards',
    };

    this.__tick.animate(keyFrames, timing);
  }

  private __applyRegexCardValue(target: WaInput) {
    const value = (target.value ?? '').replace(/\D/g, '');
    target.value = value.replace(/(\d{4})(?=\d)/g, '$1 ');
  }

  render() {
    const fieldStyles = {
      '--ns-input-height': this.height ? `${this.height}px` : undefined,
      '--ns-input-width': this.width ? `${this.width}px` : undefined,
    };

    return html`
      <div class="layout ${this.stacked ? 'vertical' : 'horizontal center'}">
        <div
          class="label small ${this.stacked ? 'mb-1' : 'mr-1'} ${this.hideLabel ? 'visually-hidden' : ''}"
          aria-hidden="true"
        >
          ${this.label}
        </div>

        <div class="wrapper flex">
          <wa-input
            id="input-field"
            style=${styleMap(fieldStyles)}
            label=${this.label ?? ''}
            autocomplete=${this.autocomplete ?? 'off'}
            type=${this.type === 'card-number' ? 'tel' : (this.type ?? 'text')}
            placeholder=${this.placeholder ?? ''}
            .value=${this.value ?? ''}
            ?required=${this.required}
            ?disabled=${this.disabled}
            pill
            ?with-clear=${this.clearable}
            ?without-spin-buttons=${this.type === 'number'}
            @input=${this.__onInput}
          ></wa-input>

          <div id="tick">
            <div class="layout horizontal end-justified center h-100">
              <div class="layout horizontal center-center tick-mark mr-1">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="-0.07 -0.07 5.63 4.13">
                  <path d="M0.176758 2.30176L1.67676 3.80176L5.30176 0.176758" stroke="#000000" stroke-width="0.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
